import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Text,
  Card,
  Tabs,
  Button,
  Badge,
  EmptyState,
  Table,
} from '@teals/ui-web';
import type { TabItem, Column } from '@teals/ui-web';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  useGetStaffAnalyticsSummaryQuery,
  useGetStaffAnalyticsTimeseriesQuery,
} from '../../store/api/staffApi';
import {
  Banner,
  ControlsBar,
  ControlLabel,
  Footnote,
  KpiCard,
  KpiLabel,
  KpiPeriodHint,
  KpiStrip,
  KpiValue,
  OperatorBadgeRow,
  Page,
  PanelCard,
  RangeText,
  SectionSub,
  SectionTitle,
  Subtitle,
} from './Staff.styles';
import {
  STAFF_PERIOD_PRESETS,
  staffPeriodGrain,
  staffPeriodLabel,
  staffPeriodRange,
  type StaffPeriodId,
} from './staffPeriod';
import { PricingPanel } from './PricingPanel';
import { formatChartBucket } from '../Analytics/analyticsFormat';

type StaffTab =
  | 'assume-admin'
  | 'cross-org'
  | 'create-claim'
  | 'orgs-users'
  | 'notifications'
  | 'pricing';

type OrgRow = {
  orgId: string;
  orgName: string;
  tokensTotal: number;
  sessions: number;
  platformCostUsd: number;
  estSubscriptionUsd: number;
  estUsageUsd: number;
  estEarningsUsd: number;
  marginUsd: number;
};

function money(n: number | undefined | null): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function tokens(n: number | undefined | null): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

function WipPanel({ title }: { title: string }) {
  return (
    <Card style={{ padding: '1.5rem', marginTop: '1rem' }}>
      <EmptyState
        title={`${title} — work in progress`}
        description="This Staff Console tab is scaffolded for a later pass."
      />
    </Card>
  );
}

function formatRangeLabel(from: string, to: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  };
  return `${new Date(from).toLocaleDateString(undefined, opts)} – ${new Date(
    to,
  ).toLocaleDateString(undefined, opts)}`;
}

function CrossOrgAnalyticsPanel() {
  const [periodId, setPeriodId] = useState<StaffPeriodId>('this_month');
  const { from, to } = useMemo(() => staffPeriodRange(periodId), [periodId]);
  const grain = staffPeriodGrain(periodId);
  const periodLabel = staffPeriodLabel(periodId);
  const rangeLabel = useMemo(() => formatRangeLabel(from, to), [from, to]);

  const { data, isLoading, error, refetch } = useGetStaffAnalyticsSummaryQuery({
    from,
    to,
  });
  const { data: series } = useGetStaffAnalyticsTimeseriesQuery({
    from,
    to,
    grain,
    metric: 'tokens_total',
  });

  const chartData = useMemo(
    () =>
      (series?.points || []).map((p) => ({
        bucket: p.bucket,
        tokens: p.value,
      })),
    [series?.points],
  );

  const orgRows: OrgRow[] = useMemo(() => {
    const rows = (data?.organisations || []).map((o) => ({
      orgId: o.orgId,
      orgName: o.orgName,
      tokensTotal: o.tokensTotal ?? 0,
      sessions: o.sessions ?? 0,
      platformCostUsd: o.platformCostUsd ?? 0,
      estSubscriptionUsd: o.estSubscriptionUsd ?? 0,
      estUsageUsd: o.estUsageUsd ?? 0,
      estEarningsUsd: o.estEarningsUsd ?? 0,
      marginUsd: o.marginUsd ?? 0,
    }));
    return rows.sort((a, b) => b.tokensTotal - a.tokensTotal);
  }, [data?.organisations]);

  const orgColumns: Column<OrgRow>[] = useMemo(
    () => [
      {
        key: 'orgName',
        header: 'Organisation',
        render: (_val: unknown, row: OrgRow) => (
          <Text variant="body2" style={{ fontWeight: 500 }}>
            {row.orgName}
          </Text>
        ),
      },
      {
        key: 'tokensTotal',
        header: 'Tokens',
        render: (_val: unknown, row: OrgRow) => <>{tokens(row.tokensTotal)}</>,
      },
      {
        key: 'sessions',
        header: 'Sessions',
        render: (_val: unknown, row: OrgRow) => <>{tokens(row.sessions)}</>,
      },
      {
        key: 'estSubscriptionUsd',
        header: 'Est. subscription',
        render: (_val: unknown, row: OrgRow) => (
          <>{money(row.estSubscriptionUsd)}</>
        ),
      },
      {
        key: 'estUsageUsd',
        header: 'Est. usage',
        render: (_val: unknown, row: OrgRow) => <>{money(row.estUsageUsd)}</>,
      },
      {
        key: 'estEarningsUsd',
        header: 'Est. total',
        render: (_val: unknown, row: OrgRow) => <>{money(row.estEarningsUsd)}</>,
      },
      {
        key: 'platformCostUsd',
        header: 'Internal LLM cost',
        render: (_val: unknown, row: OrgRow) => (
          <>{money(row.platformCostUsd)}</>
        ),
      },
      {
        key: 'marginUsd',
        header: 'Margin',
        render: (_val: unknown, row: OrgRow) => <>{money(row.marginUsd)}</>,
      },
    ],
    [],
  );

  if (isLoading) {
    return <Text variant="body2">Loading cross-org analytics…</Text>;
  }
  if (error) {
    return (
      <EmptyState
        title="Could not load staff analytics"
        description="Check you are in the Cognito staff group and try again."
        action={<Button onClick={() => refetch()}>Retry</Button>}
      />
    );
  }

  const maxTokens = Math.max(0, ...chartData.map((d) => d.tokens));

  return (
    <div>
      <OperatorBadgeRow>
        <Badge variant="warning">Operator $ view</Badge>
        <span>Internal LLM cost and earnings across organisations.</span>
      </OperatorBadgeRow>

      <Banner>
        <strong>Operator view — monetary figures are internal only</strong>
        <span>
          Customers see tokens on Analytics and subscription/usage charges on
          Payments. LLM cost, margin, and est. earnings stay on Staff.
        </span>
      </Banner>

      <ControlsBar role="group" aria-label="Period">
        <ControlLabel>Period</ControlLabel>
        {STAFF_PERIOD_PRESETS.map((p) => (
          <Button
            key={p.id}
            size="sm"
            variant={periodId === p.id ? 'primary' : 'outline'}
            onClick={() => setPeriodId(p.id)}
          >
            {p.label}
          </Button>
        ))}
        <RangeText>{rangeLabel} (UTC)</RangeText>
      </ControlsBar>

      <KpiStrip>
        <KpiCard>
          <KpiLabel>Orgs active</KpiLabel>
          <KpiValue>{tokens(data?.activeOrgs)}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>
            Tokens <KpiPeriodHint>· {periodLabel.toLowerCase()}</KpiPeriodHint>
          </KpiLabel>
          <KpiValue>{tokens(data?.tokensTotal)}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Sessions</KpiLabel>
          <KpiValue>{tokens(data?.sessions)}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Internal LLM cost</KpiLabel>
          <KpiValue>{money(data?.platformCostUsd)}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Est. earnings</KpiLabel>
          <KpiValue>{money(data?.estEarningsUsd)}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Margin</KpiLabel>
          <KpiValue>{money(data?.marginUsd)}</KpiValue>
        </KpiCard>
      </KpiStrip>

      <PanelCard>
        <SectionTitle>Token volume</SectionTitle>
        <SectionSub>
          {grain} grain · {periodLabel.toLowerCase()} · totals for selected
          period
        </SectionSub>
        {chartData.length === 0 ? (
          <Text variant="body2">No timeseries data in this period.</Text>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="bucket"
                hide={chartData.length > 14}
                tickFormatter={(v) => formatChartBucket(String(v), grain)}
                minTickGap={28}
                interval="preserveStartEnd"
              />
              <YAxis />
              <Tooltip
                labelFormatter={(label) =>
                  formatChartBucket(String(label), grain)
                }
              />
              <Bar dataKey="tokens">
                {chartData.map((entry, i) => (
                  <Cell
                    key={`${entry.bucket}-${i}`}
                    fill={
                      entry.tokens === maxTokens && maxTokens > 0
                        ? 'var(--warning)'
                        : 'var(--accent)'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </PanelCard>

      <PanelCard>
        <SectionTitle>By organisation</SectionTitle>
        <SectionSub>
          Same period as chart · {periodLabel.toLowerCase()} · {rangeLabel}
        </SectionSub>
        {orgRows.length === 0 ? (
          <Text variant="body2">No organisation usage in this period.</Text>
        ) : (
          <Table columns={orgColumns} data={orgRows} rowKey="orgId" />
        )}
      </PanelCard>

      <Footnote>
        Monetary figures are internal / operator-only. Chart KPIs and org
        breakdown all use the selected period. Customers see tokens on Analytics
        and subscription + tiered charges on Payments — never raw LLM cost.
      </Footnote>
    </div>
  );
}

const Staff: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as StaffTab) || 'cross-org';
  const [activeTab, setActiveTab] = useState<StaffTab>(
    [
      'assume-admin',
      'cross-org',
      'create-claim',
      'orgs-users',
      'notifications',
      'pricing',
    ].includes(initialTab)
      ? initialTab
      : 'cross-org',
  );

  const tabs: TabItem[] = [
    { id: 'assume-admin', label: 'Assume Admin' },
    { id: 'cross-org', label: 'Cross-org Analytics' },
    { id: 'create-claim', label: 'Create & Claim' },
    { id: 'orgs-users', label: 'Orgs & Users' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'pricing', label: 'Pricing' },
  ];

  const onTabChange = (id: string) => {
    const next = id as StaffTab;
    setActiveTab(next);
    setSearchParams(next === 'cross-org' ? {} : { tab: next });
  };

  return (
    <Page>
      <Text variant="h2">Staff Console</Text>
      <Subtitle>
        Platform operator tools — not a customer organisation view.
      </Subtitle>
      <Banner>
        <strong>
          Luminafactory Staff · platform operator · not a customer organisation
        </strong>
        <span>
          Commercial rates and internal costs live here. Delete organisation is
          on Super User.
        </span>
      </Banner>

      <Tabs items={tabs} activeTab={activeTab} onChange={onTabChange} />

      {activeTab === 'assume-admin' && <WipPanel title="Assume Admin" />}
      {activeTab === 'cross-org' && (
        <div style={{ marginTop: '1rem' }}>
          <CrossOrgAnalyticsPanel />
        </div>
      )}
      {activeTab === 'create-claim' && <WipPanel title="Create & Claim" />}
      {activeTab === 'orgs-users' && <WipPanel title="Orgs & Users" />}
      {activeTab === 'notifications' && <WipPanel title="Notifications" />}
      {activeTab === 'pricing' && (
        <div style={{ marginTop: '1rem' }}>
          <PricingPanel />
        </div>
      )}
    </Page>
  );
};

export default Staff;
